import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/utils/utils";
import { variantClasses } from "@/components/ui/text-variants";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";
import { useClipboard } from "@dreamhub/designsystem";
import { Copy, Check } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";

export type TextVariant =
  | "smLink"
  | "xlCtaSemibold"
  | "twoXlH2"
  | "body"
  | "smBody"
  | "smBodyError"
  | "smBodyWarning"
  | "bodyWarning"
  | "lgBody"
  | "label"
  | "smLabel"
  | "title1H1"
  | "title1H2"
  | "title1H3"
  | "title2H1"
  | "title2H2"
  | "title2H3"
  | "title3H1"
  | "title3H2"
  | "title3H3"
  | "smSubtitle"
  | "lgSubtitle"
  | "lgSubtitleH1"
  | "lgSubtitleH2"
  | "lgSubtitleH3"
  | "xlH1Medium"
  | "lgLabel"
  | "bodySecondary"
  | "smBodySecondary"
  | "lgBodySecondary"
  | "labelSecondary"
  | "smLabelSecondary"
  | "lgLabelSecondary"
  | "smSubtitleSecondary"
  | "lgSubtitleSecondary"
  | "bodyTertiary"
  | "smBodyTertiary"
  | "lgBodyTertiary"
  | "labelTertiary"
  | "smLabelTertiary"
  | "lgLabelTertiary"
  | "smSubtitleTertiary"
  | "lgSubtitleTertiary";

/** Already-translated strings for the copy button. */
export interface CopyLabels {
  tooltip: string;
  copied: string;
}

// The labels are required whenever the value can be copied, so a copyable
// call site that forgets them fails to compile instead of rendering blank.
export type CopyProps =
  | { copyable?: false; copyLabels?: never }
  | { copyable: boolean; copyLabels: CopyLabels };

type TextBaseProps<C extends React.ElementType> = {
  as?: C;
  variant?: TextVariant;
  children: React.ReactNode;
  className?: string;
  hasTruncatedTooltip?: boolean;
  copyText?: string;
  // "inline" is the preview-field row treatment: the copy action sits at the
  // row's right edge with a background and reports the copy on the button,
  // matching ClickableUrlField. Everywhere else — tables, cards, QuickView —
  // keeps the compact button beside the value and the "Copied" label.
  copyVariant?: "default" | "inline";
} & Omit<
  React.ComponentPropsWithoutRef<C>,
  "as" | "variant" | "className" | keyof CopyProps
>;

type TextProps<C extends React.ElementType> = TextBaseProps<C> & CopyProps;

const Text = <C extends React.ElementType = "p">(props: TextProps<C>) => {
  // Widened from the union so the rest props keep the element's own types.
  const {
    as,
    variant = "body",
    children,
    className,
    hasTruncatedTooltip = false,
    // Copy functionality props
    copyable = false,
    copyText,
    copyLabels,
    copyVariant = "default",
    ...restProps
  }: TextBaseProps<C> & { copyable?: boolean; copyLabels?: CopyLabels } =
    props;
  const Component = as || "p";
  const ref = useRef<HTMLDivElement>(null);
  const [isOverflown, setIsOverflown] = useState(false);
  // Span-level hover drives the copy button's visibility and its hint tooltip;
  // the value tracks its own hover so the full-value tooltip appears only over
  // the text and the copy hint only over the button — never both at once.
  const [isHovered, setIsHovered] = useState(false);
  const [isValueHovered, setIsValueHovered] = useState(false);
  const [isCopyHovered, setIsCopyHovered] = useState(false);
  const isInlineCopy = copyVariant === "inline";
  // Every copyable value in the app renders this button, so its resizing waits
  // for the flag along with the fields it was drawn for.
  const isNewFieldsUI = useNewFieldsUI();

  const textToCopy = copyText || (typeof children === "string" ? children : "");
  const { hasCopied, onCopy } = useClipboard(textToCopy, { timeout: 2000 });

  const copyTooltipText = copyLabels?.tooltip ?? "";
  const copiedTextValue = copyLabels?.copied ?? "";

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCopy();
  };

  useEffect(() => {
    const checkOverflow = () => {
      const element = ref.current;
      if (element && (hasTruncatedTooltip || copyable)) {
        setIsOverflown(element.scrollWidth > element.clientWidth);
      }
    };

    checkOverflow();
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reads the ref at cleanup; capturing it at setup would change which element is unobserved
        resizeObserver.unobserve(ref.current);
      }
    };
  }, [hasTruncatedTooltip, copyable, children]);

  const renderText = (withRef = false) => {
    if (copyable) {
      return (
        <span
          className={cn(
            "relative items-center cursor-pointer h-7 min-w-0",
            // Mirror ClickableUrlField's row: the copy action sits at the
            // row's right edge instead of hugging the end of the value.
            isInlineCopy ? "flex w-full justify-between" : "inline-flex max-w-full"
          )}
          onFocus={() => setIsHovered(true)}
          onBlur={(focusEvent) => {
            // Only when focus leaves the row entirely — moving between the value
            // and the copy button must not hide the control mid-tab.
            if (!focusEvent.currentTarget.contains(focusEvent.relatedTarget)) {
              setIsHovered(false);
            }
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => {
            setIsHovered(false);
            // Leaving the row can swallow the button's own mouseleave; clear it
            // here so the hint cannot reappear on the next hover.
            setIsCopyHovered(false);
          }}
        >
          {/* Truncating the value keeps the copy button in view but stops the
              cell from overflowing, so TruncatedCell's own tooltip no longer
              fires. Reveal the full value on hover here instead, but only when
              it is actually clipped and no richer tooltip is already wired via
              hasTruncatedTooltip. */}
          <Tooltip
            open={
              !hasTruncatedTooltip && isValueHovered && isOverflown && !hasCopied
            }
          >
            <TooltipTrigger asChild>
              <Component
                ref={withRef || copyable ? ref : null}
                className={cn(
                  variantClasses[variant],
                  className,
                  isOverflown && "cursor-pointer",
                  "transition-all duration-200 min-w-0",
                  (isInlineCopy || !hasCopied) && "truncate"
                )}
                {...restProps}
                onMouseEnter={(event: React.MouseEvent) => {
                  restProps.onMouseEnter?.(event);
                  setIsValueHovered(true);
                }}
                onMouseLeave={(event: React.MouseEvent) => {
                  restProps.onMouseLeave?.(event);
                  setIsValueHovered(false);
                }}
              >
                {/* ClickableUrlField leaves its value in place and reports the
                    copy on the button alone; the redesigned rows do the same, so
                    the value no longer swaps out for a "Copied" label. */}
                {hasCopied && !isInlineCopy ? (
                  <span className="inline-flex items-center justify-center gap-1 mt-1 h-full w-full text-secondary">
                    <IconContainer size="sm" color="secondary">
                      <Check />
                    </IconContainer>
                    {copiedTextValue}
                  </span>
                ) : (
                  children
                )}
              </Component>
            </TooltipTrigger>
            {isOverflown && (
              // Render the raw value literally — never as markdown. Copyable
              // values are externally-sourced (synced contacts/leads), so
              // markdown rendering would turn injected link/image syntax into
              // clickable content inside the tooltip.
              <TooltipContent maxWidth={24} enableMarkdown={false}>
                {children}
              </TooltipContent>
            )}
          </Tooltip>

          {/* Always mounted to avoid a click-race on first hover. The hint
              tracks the button's own hover; inferring it from "row hovered but
              not the value" opened the hint across the row's empty width.
              isHovered still gates it so a swallowed mouseleave can't strand
              it open. */}
          <Tooltip open={isHovered && isCopyHovered && !hasCopied}>
            <TooltipTrigger asChild>
              <IconButton
                onClick={handleCopy}
                onMouseEnter={() => setIsCopyHovered(true)}
                onMouseLeave={() => setIsCopyHovered(false)}
                // ClickableUrlField's copy/open use "icon" — the treatment with a
                // background — so the shared copy affordance matches it.
                variant={isInlineCopy ? "icon" : "iconNoBg"}
                size={isNewFieldsUI ? "md" : "sm"}
                iconColor={isNewFieldsUI ? "secondary" : undefined}
                aria-label={
                  isInlineCopy && hasCopied ? copiedTextValue : copyTooltipText
                }
                // Hover can never put the button in the tab order: isHovered is
                // what makes it tabbable, and nothing else in the row is
                // focusable, so focus could never arrive to set it. Under the
                // flag it stays tabbable and focus-visible reveals it; flag-off
                // keeps the hover-only behaviour so no new tab stop appears on
                // every copyable value in the app.
                tabIndex={
                  (isNewFieldsUI ? false : !isHovered) ||
                  (hasCopied && !isInlineCopy)
                    ? -1
                    : undefined
                }
                className={cn(
                  "ml-1 shrink-0",
                  (!isHovered || (hasCopied && !isInlineCopy)) &&
                    "opacity-0 pointer-events-none",
                  isNewFieldsUI &&
                    "focus-visible:opacity-100 focus-visible:pointer-events-auto"
                )}
              >
                {isNewFieldsUI ? (
                  isInlineCopy && hasCopied ? (
                    <Check />
                  ) : (
                    <Copy />
                  )
                ) : (
                  <IconContainer size="sm" color="secondary">
                    <Copy />
                  </IconContainer>
                )}
              </IconButton>
            </TooltipTrigger>
            <TooltipContent>{copyTooltipText}</TooltipContent>
          </Tooltip>
        </span>
      );
    }

    return (
      <Component
        ref={withRef ? ref : null}
        className={cn(
          variantClasses[variant],
          className,
          isOverflown && "cursor-pointer"
        )}
        {...restProps}
      >
        {children}
      </Component>
    );
  };

  return hasTruncatedTooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>{renderText(true)}</TooltipTrigger>
      {isOverflown && (
        <TooltipContent maxWidth={24} enableMarkdown={false}>
          {children}
        </TooltipContent>
      )}
    </Tooltip>
  ) : (
    renderText()
  );
};

export default Text;
